export type HeaderSearchCatalogEntry = {
  id: string;
  labelKey: string;
  path: string;
  /** Lowercase ASCII tokens for matching alongside translated label */
  keywords: string[];
};

/**
 * Destinations available from the header search (Platform routes).
 * Paths match `routes.ts`. Omit entries that are temporarily hidden in `AppSidebar`
 * (quick nav, Discover items, Ad Studio block) so users cannot reach them from here.
 */
export const HEADER_SEARCH_CATALOG: HeaderSearchCatalogEntry[] = [
  { id: 'dashboard', labelKey: 'headerSearch.actions.dashboard', path: '/', keywords: ['home', 'start', 'dashboard', 'main'] },
  { id: 'creators', labelKey: 'headerSearch.actions.creators', path: '/creators', keywords: ['creators', 'influencers', 'discovery'] },
  { id: 'creator-lists', labelKey: 'headerSearch.actions.creatorLists', path: '/creator-lists', keywords: ['lists', 'creatorlists', 'creator', 'lists'] },
  { id: 'search', labelKey: 'headerSearch.actions.influencerSearch', path: '/creators/search', keywords: ['search', 'find', 'influencer', 'results'] },
  { id: 'content-hub', labelKey: 'headerSearch.actions.contentHub', path: '/content-hub', keywords: ['content', 'hub', 'library'] },
  { id: 'campaigns', labelKey: 'headerSearch.actions.campaigns', path: '/campaigns', keywords: ['campaigns', 'campaign', 'all'] },
  { id: 'new-campaign', labelKey: 'headerSearch.actions.newCampaign', path: '/campaigns/new', keywords: ['new', 'create', 'campaign', 'planner'] },
  { id: 'performance', labelKey: 'headerSearch.actions.performance', path: '/performance', keywords: ['performance', 'metrics', 'results'] },
  { id: 'brand-lift', labelKey: 'headerSearch.actions.brandLift', path: '/brand-lift', keywords: ['brand', 'lift'] },
  { id: 'mmm', labelKey: 'headerSearch.actions.mmm', path: '/marketing-mix-model', keywords: ['mmm', 'marketing', 'mix', 'model'] },
  { id: 'awards', labelKey: 'headerSearch.actions.awards', path: '/awards', keywords: ['awards', 'trophies', 'trophy'] },
  { id: 'ambassador', labelKey: 'headerSearch.actions.ambassador', path: '/ambassador-hub', keywords: ['ambassador', 'hub'] },
  { id: 'drafts', labelKey: 'headerSearch.actions.drafts', path: '/drafts', keywords: ['drafts', 'draft'] },
  { id: 'casting', labelKey: 'headerSearch.actions.casting', path: '/casting', keywords: ['casting', 'cast'] },
  { id: 'strategies', labelKey: 'headerSearch.actions.strategies', path: '/strategies', keywords: ['strategies', 'strategy'] },
  { id: 'settings', labelKey: 'headerSearch.actions.settings', path: '/settings', keywords: ['settings', 'preferences', 'account'] },
  { id: 'team', labelKey: 'headerSearch.actions.team', path: '/team', keywords: ['team', 'members'] },
  { id: 'dev-mode', labelKey: 'headerSearch.actions.devMode', path: '/dev-mode', keywords: ['dev', 'developer', 'debug'] },
];

/** Nav paths the header search may suggest or restore from recents (sync with catalog). */
const ALLOWED_HEADER_NAV_PATHS = new Set(HEADER_SEARCH_CATALOG.map((e) => e.path));

/** When path is /dev-mode, requires isAdminJwt (Beatly admin JWT) to be allowed in search/recents. */
export function isAllowedHeaderSearchNavPath(path: string, isAdminJwt = false): boolean {
  if (path === '/dev-mode' && !isAdminJwt) return false;
  return ALLOWED_HEADER_NAV_PATHS.has(path);
}

export function filterHeaderSearchCatalog(
  rawQuery: string,
  translate: (key: string) => string,
  options?: { includeDevMode?: boolean }
): HeaderSearchCatalogEntry[] {
  const includeDevMode = options?.includeDevMode ?? false;
  const q = rawQuery.trim().toLowerCase();
  if (!q) return [];

  return HEADER_SEARCH_CATALOG.filter((entry) => {
    if (entry.id === 'dev-mode' && !includeDevMode) return false;
    const label = translate(entry.labelKey).toLowerCase();
    if (label.includes(q)) return true;
    const words = q.split(/\s+/).filter(Boolean);
    if (words.length > 0 && words.every((w) => label.includes(w))) return true;
    return entry.keywords.some(
      (k) => k.includes(q) || q.includes(k) || words.some((w) => k.includes(w) || w.includes(k))
    );
  });
}
